/**
 * Zoom and pan of a picture in the photo viewer. The picture is drawn to fit its box (object-fit: contain) and then
 * scaled about the box centre and moved by (x, y) screen pixels; points are relative to the box centre.
 */

export interface Size {
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface View {
  scale: number;
  x: number;
  y: number;
}

export const REST: View = { scale: 1, x: 0, y: 0 };
export const MAX_SCALE = 5;
/** A double tap or double click zooms in this far, or back out. */
export const TAP_SCALE = 2.5;

/** The picture's size on screen at scale 1: fitted into the box, never enlarged past its own pixels. */
export function fitted(natural: Size, box: Size): Size {
  if (!natural.w || !natural.h || !box.w || !box.h) return { w: box.w, h: box.h };
  const ratio = Math.min(box.w / natural.w, box.h / natural.h, 1);
  return { w: natural.w * ratio, h: natural.h * ratio };
}

// `|| 0`: a -0 left by the arithmetic reads as 0.
const limit = (value: number, max: number) => Math.min(max, Math.max(-max, value)) || 0;

/** Keeps the picture covering the box where it is larger than it, and centred where it is smaller. */
export function clamp(view: View, picture: Size, box: Size): View {
  const scale = Math.min(MAX_SCALE, Math.max(1, view.scale));
  return {
    scale,
    x: limit(view.x, Math.max(0, (picture.w * scale - box.w) / 2)),
    y: limit(view.y, Math.max(0, (picture.h * scale - box.h) / 2)),
  };
}

/** Zooms to `scale` keeping the picture point under `at` where it is, as a pinch or the wheel does. */
export function zoomAt(view: View, scale: number, at: Point, picture: Size, box: Size): View {
  const next = Math.min(MAX_SCALE, Math.max(1, scale));
  const ratio = next / view.scale;
  return clamp({ scale: next, x: at.x - (at.x - view.x) * ratio, y: at.y - (at.y - view.y) * ratio }, picture, box);
}

/**
 * A pinch: the scale follows the distance between the fingers and the picture follows their midpoint.
 * `from` is the view, `start` the midpoint and `distance` the spread when the second finger came down.
 */
export function pinch(from: View, start: { mid: Point; distance: number }, now: { mid: Point; distance: number }, picture: Size, box: Size): View {
  const scale = Math.min(MAX_SCALE, Math.max(1, (from.scale * now.distance) / (start.distance || 1)));
  const ratio = scale / from.scale;
  return clamp({ scale, x: now.mid.x - (start.mid.x - from.x) * ratio, y: now.mid.y - (start.mid.y - from.y) * ratio }, picture, box);
}

/** Whether a point is on the picture itself rather than the empty space around it. */
export function onPicture(view: View, picture: Size, at: Point): boolean {
  return Math.abs(at.x - view.x) <= (picture.w * view.scale) / 2 && Math.abs(at.y - view.y) <= (picture.h * view.scale) / 2;
}

/** Where a swipe ends: the next or previous picture when it went far or fast enough, else back to this one. */
export function swipeTarget(index: number, count: number, dx: number, velocity: number, width: number): number {
  const far = Math.abs(dx) > width * 0.2;
  const fast = Math.abs(velocity) > 0.45 && Math.abs(dx) > 24;
  if (!far && !fast) return index;
  const next = dx < 0 ? index + 1 : index - 1;
  return Math.min(count - 1, Math.max(0, next));
}
